// ./src/components/generic/AppModal/AppModal.tsx
import React from "react";
import { Modal, Text, Group, Divider, Box, Alert } from "@mantine/core";
import { AppButton } from "../AppButton";
import { AppIcon } from "../AppIcon";

export interface AppModalAction {
  /** Button label */
  label: string;

  /** Button variant */
  variant?: "primary" | "secondary" | "tertiary" | "danger";

  /** Button icon */
  iconName?: string;

  /** Click handler */
  onClick: () => void;

  /** Loading state */
  inProgress?: boolean;

  /** Disabled state */
  disabled?: boolean;
}

export interface AppModalProps {
  /** Whether the modal is open */
  opened: boolean;

  /** Function to close the modal */
  onClose: () => void;

  onConfirm?: () => void;

  /** Modal title */
  title: string;

  /** Optional description/subtitle */
  description?: string;

  /** Modal content */
  children: React.ReactNode;

  /** Array of action buttons */
  actions?: AppModalAction[];

  /** Quick setup for common button combinations */
  actionType?: "ok" | "close" | "confirm-cancel" | "save-cancel" | "custom";

  /** Size of the modal */
  size?: "xs" | "sm" | "md" | "lg" | "xl" | "auto" | string;

  /** Whether modal can be closed by clicking outside or pressing escape */
  closable?: boolean;

  /** Loading state that affects all interactions */
  loading?: boolean;

  /** Additional className for styling */
  className?: string;

  /** Additional styles */
  style?: React.CSSProperties;

  /** Custom footer content (overrides action buttons) */
  footer?: React.ReactNode;

  /** Whether to show divider between content and footer */
  withDivider?: boolean;

  /** Error message(s) to display above actions */
  errors?: string | string[] | React.ReactNode;
}

export const AppModal: React.FC<AppModalProps> = ({
  opened,
  onClose,
  onConfirm,
  title,
  description,
  children,
  actions,
  actionType = "custom",
  size = "md",
  closable = true,
  loading = false,
  className,
  style,
  footer,
  withDivider = true,
  errors,
}) => {
  // Generate default actions based on actionType
  const getDefaultActions = (): AppModalAction[] => {
    switch (actionType) {
      case "ok":
        return [
          {
            label: "OK",
            variant: "primary",
            onClick: onClose,
          },
        ];

      case "close":
        return [
          {
            label: "Close",
            variant: "secondary",
            onClick: onClose,
          },
        ];

      case "confirm-cancel":
        return [
          {
            label: "Cancel",
            variant: "secondary",
            onClick: onClose,
          },
          {
            label: "Confirm",
            variant: "primary",
            onClick: onConfirm ?? onClose,
          },
        ];

      case "save-cancel":
        return [
          {
            label: "Cancel",
            variant: "secondary",
            onClick: onClose,
          },
          {
            label: "Save",
            variant: "primary",
            iconName: "Check",
            onClick: onConfirm ?? onClose,
          },
        ];

      default:
        return [];
    }
  };

  const finalActions = actions || getDefaultActions();
  const canClose = closable && !loading;

  const handleClose = () => {
    if (canClose) {
      onClose();
    }
  };

  const renderErrors = () => {
    if (!errors) return null;

    // If errors is a React node, render it directly
    if (React.isValidElement(errors)) {
      return (
        <Box px="md" pb="md">
          {errors}
        </Box>
      );
    }

    // Handle string or array of strings
    const errorArray = Array.isArray(errors) ? errors : [errors];

    if (errorArray.length === 0) return null;

    return (
      <Box px="md" pb="md">
        <Alert
          color="red"
          icon={<AppIcon iconName="AlertCircle" size={16} />}
          styles={{
            root: {
              padding: "0.75rem",
            },
          }}
        >
          {errorArray.length === 1 ? (
            <Text size="sm">{errorArray[0]}</Text>
          ) : (
            <Box component="ul" style={{ margin: 0, paddingLeft: "1.25rem" }}>
              {errorArray.map((error, index) => (
                <li key={index}>
                  <Text size="sm">{error}</Text>
                </li>
              ))}
            </Box>
          )}
        </Alert>
      </Box>
    );
  };

  const renderActions = () => {
    if (footer) {
      return footer;
    }

    if (finalActions.length === 0) {
      return null;
    }

    return (
      <>
        {withDivider && <Divider />}
        <Group justify="flex-end" gap="sm" p="md">
          {finalActions.map((action, index) => (
            <AppButton
              key={index}
              variant={action.variant || "secondary"}
              iconName={action.iconName}
              onClick={action.onClick}
              inProgress={action.inProgress || loading}
              disabled={action.disabled || loading}
            >
              {action.label}
            </AppButton>
          ))}
        </Group>
      </>
    );
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={title}
      size={size}
      centered
      closeOnClickOutside={false}
      closeOnEscape={canClose}
      withCloseButton={canClose}
      {...(className && { className })}
      style={style}
      styles={{
        body: {
          padding: 0,
          display: "flex",
          flexDirection: "column",
          maxHeight: "calc(100vh - 8rem)", // Reserve space for modal margins
        },
      }}
    >
      {/* Description Section (if present) - Fixed */}
      {description && (
        <Box px="md" pt="md">
          <Text size="md" c="dimmed">
            {description}
          </Text>
        </Box>
      )}

      {/* Scrollable Content */}
      <Box
        style={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          minHeight: 0, // Important for flex scrolling
        }}
        p="md"
      >
        {children}
      </Box>

      {/* Error Section (if present) - Fixed */}
      {renderErrors()}

      {/* Footer - Fixed */}
      {renderActions()}
    </Modal>
  );
};
